import { css, Theme } from '@emotion/react';
import { Link, PencilSimple } from '@phosphor-icons/react';
import { listComponentEnvironmentVariables } from 'common/stack/stackEnvUtils';
import { importComponentVariables } from 'common/stack/stackImportEnvs';
import { Accordion } from 'client/components/Accordion';
import { Help } from 'client/components/Help';
import { Tag } from 'client/components/Tag';
import { Environment } from 'client/sections/environment/Environment';
import { ImportedEnvironment } from 'client/sections/environment/ImportedEnvironment';
import { useProjectOrDeploymentState } from 'client/sections/sectionsUtils';
import { useComponent } from './componentUtils';

export const ComponentEnvironment = ({ stackComponentID }: { stackComponentID: number }) => {
  const stackConfig = useProjectOrDeploymentState((state) => state.document?.stackConfig);
  const componentsWithValues = useProjectOrDeploymentState((state) => state.document?.componentsWithValues || []);
  const component = useComponent(stackComponentID);

  if (!stackConfig) return null;
  if (!component) return null;

  const importedVariables = importComponentVariables({ stackConfig, components: componentsWithValues, componentWithValues: component });
  const automaticEnvs = listComponentEnvironmentVariables({ component, importedVariables });

  return (
    <Accordion title="Environment" description="View & copy environment variables" hideToggle css={accordionStyle}>
      <div css={contentStyle}>
        {automaticEnvs.length > 0 && (
          <ImportedEnvironment
            stackComponentID={stackComponentID}
            label={
              <>
                <Link size={18} weight="bold" /> Automatic
              </>
            }
            values={automaticEnvs}
          >
            <Help css={helpStyle}>
              Some values come from other components based on the configuration in <Tag>diploi.yaml</Tag>.
            </Help>
          </ImportedEnvironment>
        )}
        <Environment
          stackComponentID={stackComponentID}
          label={
            <>
              <PencilSimple size={18} weight="bold" /> Custom
            </>
          }
          path="configuration.environmentVariables"
        />
      </div>
    </Accordion>
  );
};

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const helpStyle = css`
  margin-bottom: 0;
`;

const accordionStyle = (theme: Theme) => css`
  width: calc(100% + 200px);
  background-color: transparent;
  border-bottom: none;
  transform: translateX(-200px);

  &::after {
    content: '';
    display: block;
    position: absolute;
    right: 0;
    height: 2px;
    width: 65ch;
    background-color: ${theme.color.grey[60]};
  }

  > section {
    > header,
    > div {
      padding-left: 200px;
    }
  }
`;
